import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import { FormField } from '../molecues/FormField';
import { Checkbox } from '../atoms';
import type { Language, CreateLanguageRequest, UpdateLanguageRequest } from '../../types/api/language';

/**
 * LanguageFormModal Component
 * 
 * Modal form for creating and editing languages
 */

export interface LanguageFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    payload: CreateLanguageRequest | UpdateLanguageRequest;
    iconFile: File | null;
  }) => void | Promise<void>;
  mode: 'create' | 'edit';
  language?: Language;
}

export const LanguageFormModal: React.FC<LanguageFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  language,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    nameMm: '',
    nativeName: '',
    isoCode: '',
    description: '',
    colorCode: '#EAB308',
    isActive: true,
    isRtl: false,
    sortOrder: 1,
  });

  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form when editing
  useEffect(() => {
    if (language && mode === 'edit') {
      setFormData({
        name: language.name || '',
        nameMm: language.nameMm || '',
        nativeName: language.nativeName || '',
        isoCode: language.isoCode || '',
        description: language.description || '',
        colorCode: language.colorCode || '#EAB308',
        isActive: language.isActive ?? true,
        isRtl: language.isRtl ?? false,
        sortOrder: language.sortOrder || 1,
      });
      setIconPreview(language.iconUrl || null);
      setIconFile(null);
    } else {
      // Reset form for create mode
      setFormData({
        name: '',
        nameMm: '',
        nativeName: '',
        isoCode: '',
        description: '',
        colorCode: '#EAB308',
        isActive: true,
        isRtl: false,
        sortOrder: 1,
      });
      setIconFile(null);
      setIconPreview(null);
    }
    setErrors({});
  }, [language, mode, isOpen]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (mode === 'create') {
      if (!formData.name.trim()) {
        newErrors.name = 'Name is required';
      }
      if (!formData.nameMm.trim()) {
        newErrors.nameMm = 'Name (Myanmar) is required';
      }
      if (!formData.nativeName.trim()) {
        newErrors.nativeName = 'Native name is required';
      }
      if (!formData.isoCode.trim()) {
        newErrors.isoCode = 'ISO code is required';
      }
      if (formData.isoCode.length !== 2) {
        newErrors.isoCode = 'ISO code must be 2 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        payload: formData,
        iconFile,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.match('image/(jpeg|png|webp)')) {
      setErrors((prev) => ({ ...prev, iconFile: 'Allowed formats: JPEG, PNG, WebP' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, iconFile: 'Max size is 5MB' }));
      return;
    }

    setIconFile(file);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.iconFile;
      return newErrors;
    });

    const reader = new FileReader();
    reader.onloadend = () => {
      setIconPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    input.value = '';
  };

  const handleRemoveIcon = () => {
    setIconFile(null);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.iconFile;
      return newErrors;
    });
    if (mode === 'create') {
      setIconPreview(null);
    } else {
      setIconPreview(language?.iconUrl || null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Add New Language' : 'Edit Language'}
      size="lg"
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            type="submit"
          >
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={errors.name}
            required
            placeholder="e.g., English"
          />
          
          <FormField
            label="Name (Myanmar)"
            value={formData.nameMm}
            onChange={(e) => handleChange('nameMm', e.target.value)}
            error={errors.nameMm}
            required
            placeholder="e.g., အင်္ဂလိပ်"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Native Name"
            value={formData.nativeName}
            onChange={(e) => handleChange('nativeName', e.target.value)}
            error={errors.nativeName}
            required={mode === 'create'}
            placeholder="e.g., English"
          />
          
          <FormField
            label="ISO Code"
            value={formData.isoCode}
            onChange={(e) => handleChange('isoCode', e.target.value.toLowerCase())}
            error={errors.isoCode}
            required={mode === 'create'}
            placeholder="e.g., en"
            maxLength={2}
            helperText="2-letter ISO code (e.g., en, ko, my)"
          />
        </div>

        <div className="w-full">
          <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Language description"
            rows={3}
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
          />
          {errors.description && (
            <p className="text-sm text-red-600 mt-1.5">{errors.description}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Language Icon
            </label>
            <div className="space-y-2">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              />
              {errors.iconFile && (
                <p className="text-sm text-red-600">{errors.iconFile}</p>
              )}
              <p className="text-xs text-gray-500">Max size: 5MB | Formats: JPEG, PNG, WebP</p>
              {iconPreview && (
                <div className="relative inline-block mt-2">
                  <img
                    src={iconPreview}
                    alt="Icon preview"
                    className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveIcon}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                    aria-label="Remove icon"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <FormField
            label="Color Code"
            type="color"
            value={formData.colorCode}
            onChange={(e) => handleChange('colorCode', e.target.value)}
            error={errors.colorCode}
            helperText="Color for language display"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Sort Order"
            type="number"
            value={formData.sortOrder.toString()}
            onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 1)}
            error={errors.sortOrder}
            min={1}
            helperText="Display order (lower numbers appear first)"
          />
        </div>

        <div className="flex flex-col gap-3">
          <Checkbox
            checked={formData.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
            label="Active"
            helperText="Language will be available for use"
          />
          
          <Checkbox
            checked={formData.isRtl}
            onChange={(e) => handleChange('isRtl', e.target.checked)}
            label="Right-to-Left (RTL)"
            helperText="Enable RTL text direction"
          />
        </div>
      </form>
    </Modal>
  );
};

